/**
 * Austrian VAT identification numbers (UID-Nummer).
 *
 * Format: "ATU" plus eight digits, the last of which is a check digit. Getting this wrong is
 * expensive: without a valid supplier UID on an invoice above 400 € there is no input tax
 * deduction (§ 12 in connection with § 11 UStG), so the app checks it as soon as it is typed.
 */

/** Strips spaces, dots and dashes and upper-cases. */
export function normalizeVatId(value: string): string {
  return value.toUpperCase().replace(/[^A-Z0-9]/g, "");
}

/** "ATU12345675" -> "ATU 1234 5675", the way it is printed on Austrian invoices. */
export function formatVatId(value: string): string {
  const normalized = normalizeVatId(value);
  if (!/^ATU\d{8}$/.test(normalized)) return normalized;
  return `ATU ${normalized.slice(3, 7)} ${normalized.slice(7)}`;
}

/**
 * Validates the check digit of an Austrian UID.
 * Every second digit is doubled and its digits summed; the check digit makes the total 96 mod 10.
 */
export function isValidAustrianVatId(value: string): boolean {
  const normalized = normalizeVatId(value);
  if (!/^ATU\d{8}$/.test(normalized)) return false;

  const digits = [...normalized.slice(3)].map(Number);
  let sum = 0;
  for (let index = 0; index < 7; index += 1) {
    const digit = digits[index]!;
    if (index % 2 === 0) {
      sum += digit;
    } else {
      const doubled = digit * 2;
      sum += Math.floor(doubled / 10) + (doubled % 10);
    }
  }
  return (96 - sum) % 10 === digits[7];
}

/** Country codes that issue an EU VAT id, plus XI for Northern Ireland. */
const EU_COUNTRIES = new Set([
  "AT",
  "BE",
  "BG",
  "CY",
  "CZ",
  "DE",
  "DK",
  "EE",
  "EL",
  "ES",
  "FI",
  "FR",
  "HR",
  "HU",
  "IE",
  "IT",
  "LT",
  "LU",
  "LV",
  "MT",
  "NL",
  "PL",
  "PT",
  "RO",
  "SE",
  "SI",
  "SK",
  "XI",
]);

/**
 * Loose check for a non-Austrian VAT id: the right country code and a sensible length.
 * Only Austria's check digit can be verified offline; the rest needs VIES.
 */
export function isPlausibleEuVatId(value: string): boolean {
  const normalized = normalizeVatId(value);
  const country = normalized.slice(0, 2);
  if (!EU_COUNTRIES.has(country)) return false;
  if (country === "AT") return isValidAustrianVatId(normalized);
  return /^[A-Z0-9]{8,12}$/.test(normalized.slice(2));
}

/** True when a VAT id belongs to another EU member state - the reverse charge / ig case. */
export function isForeignEuVatId(value: string): boolean {
  const normalized = normalizeVatId(value);
  return normalized.slice(0, 2) !== "AT" && isPlausibleEuVatId(normalized);
}
